import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useSchool } from '@/contexts/SchoolContext';
import {
  commitCalendarPhaseAudienceReview,
  loadCalendarAudienceOptions,
  loadCalendarPhaseAudienceReview,
  previewCalendarPhaseAudienceReview,
} from '@/services/calendar/calendarAudienceService';
import { audienceLabel, inspectPhaseAudience } from '../utils/calendarAudienceUtils';

function formatEventDate(event) {
  const start = event.startDate?.toDate?.() || new Date(event.startDate);
  const end = event.endDate?.toDate?.() || new Date(event.endDate);

  if (event.allDay) {
    const startStr = format(start, 'EEE, d MMM yyyy');
    const endStr = format(end, 'EEE, d MMM yyyy');
    return startStr === endStr
      ? `All day · ${startStr}`
      : `All day · ${startStr} – ${endStr}`;
  }

  const sameDay = format(start, 'yyyy-MM-dd') === format(end, 'yyyy-MM-dd');
  if (sameDay) {
    return `${format(start, 'EEE, d MMM yyyy')} · ${format(start, 'HH:mm')} – ${format(end, 'HH:mm')}`;
  }
  return `${format(start, 'd MMM HH:mm')} – ${format(end, 'd MMM HH:mm')}`;
}

/**
 * CalendarEventDetail - Shows event details in the sidebar.
 */
export default function CalendarEventDetail({
  event,
  onEdit,
  canEdit,
  canLoadAudienceOptions = canEdit,
  onAudienceChanged,
}) {
  const { schoolId } = useSchool();
  const [phases, setPhases] = useState([]);
  const [review, setReview] = useState(null);
  const [reviewState, setReviewState] = useState('idle');
  const [reviewError, setReviewError] = useState(null);

  useEffect(() => {
    let active = true;
    if (!canLoadAudienceOptions || !schoolId || event?.audience?.scope !== 'phases') return undefined;
    loadCalendarAudienceOptions(schoolId)
      .then((result) => { if (active) setPhases(result.phases || []); })
      .catch(() => { if (active) setPhases([]); });
    return () => { active = false; };
  }, [canLoadAudienceOptions, schoolId, event?.id, event?.audience?.scope]);

  if (!event) return null;

  const phaseAudience = inspectPhaseAudience(event.audience);
  const displayAudience = event.audienceDisplayLabel
    || audienceLabel(event.audience, phases);

  const loadReview = async () => {
    setReviewError(null);
    setReviewState('loading');
    try {
      const result = await loadCalendarPhaseAudienceReview({ schoolId, eventId: event.id });
      setReview({ ...result, schoolId, eventId: event.id });
      setReviewState('loaded');
    } catch (error) {
      setReviewError(error.message);
      setReviewState('idle');
    }
  };

  const previewReview = async () => {
    setReviewError(null);
    setReviewState('previewing');
    try {
      const result = await previewCalendarPhaseAudienceReview(review);
      setReview({ ...result, schoolId, eventId: event.id });
      setReviewState('previewed');
    } catch (error) {
      setReviewError(error.message);
      setReviewState('loaded');
    }
  };

  const confirmReview = async () => {
    setReviewError(null);
    setReviewState('saving');
    try {
      await commitCalendarPhaseAudienceReview({
        ...review,
        requestId: `calendar-cutover-${crypto.randomUUID()}`,
      });
      setReviewState('saved');
      onAudienceChanged?.();
    } catch (error) {
      setReviewError(error.message);
      setReviewState('previewed');
    }
  };

  return (
    <div className="card bg-base-100 border border-base-200">
      <div className="card-body p-4 space-y-2">
        <h3 className="card-title text-base">{event.title}</h3>

        <p className="text-sm text-base-content/70">
          {formatEventDate(event)}
        </p>

        {event.description && (
          <p className="text-sm">{event.description}</p>
        )}

        <div className="flex flex-wrap gap-2 text-xs">
          <span className="badge badge-ghost badge-sm capitalize">{event.type?.replace('-', ' ')}</span>
          <span className="badge badge-ghost badge-sm">
            {displayAudience}
          </span>
        </div>

        {canEdit && phaseAudience.representation === 'legacy' && reviewState !== 'saved' && (
          <div className="mt-2 space-y-3 rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm">
            {!review && (
              <div className="flex items-center justify-between gap-3">
                <p>This event still uses an older phase audience.</p>
                <button
                  type="button"
                  className="btn btn-sm btn-outline"
                  onClick={loadReview}
                  disabled={reviewState === 'loading'}
                >
                  {reviewState === 'loading' ? <span className="loading loading-spinner loading-xs" /> : 'Review audience'}
                </button>
              </div>
            )}
            {review && !review.canPreview && (
              <p>{review.blocker}</p>
            )}
            {review?.canPreview && (
              <>
                <div>
                  <p className="font-medium">Audience change</p>
                  <p className="mt-1 text-base-content/65">
                    {review.currentLabels.join(', ')} → {review.proposedLabels.join(', ')}
                  </p>
                </div>
                {!review.comparison && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline"
                    onClick={previewReview}
                    disabled={reviewState === 'previewing'}
                  >
                    {reviewState === 'previewing' ? <span className="loading loading-spinner loading-xs" /> : 'Compare recipients'}
                  </button>
                )}
                {review.comparison && (
                  <div className="space-y-2">
                    <p>
                      {review.comparison.beforeCount} {review.comparison.beforeCount === 1 ? 'family' : 'families'} now
                      {' → '}
                      {review.comparison.afterCount} after
                      {review.comparison.addedCount || review.comparison.removedCount
                        ? ` (${review.comparison.addedCount} added, ${review.comparison.removedCount} removed)`
                        : ' (no change)'}
                    </p>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={confirmReview}
                      disabled={reviewState === 'saving'}
                    >
                      {reviewState === 'saving' ? <span className="loading loading-spinner loading-xs" /> : 'Confirm audience change'}
                    </button>
                  </div>
                )}
              </>
            )}
            {reviewError && <p className="text-error">{reviewError}</p>}
          </div>
        )}

        {reviewState === 'saved' && (
          <p className="mt-2 rounded-lg bg-success/10 px-3 py-2 text-sm text-success">
            Audience changed to current school phases.
          </p>
        )}

        {canEdit && (
          <div className="card-actions justify-end pt-2">
            <button
              className="btn btn-sm btn-ghost"
              onClick={() => onEdit?.(event)}
            >
              Edit
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
